import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Phone, MapPin, FileText, CurrencyDollar, ChartLineUp,
  Users, Buildings, Target, Warning, CircleNotch, ListChecks, MagnifyingGlass,
  FilePdf, FileXls, Calendar, BellRinging, CheckCircle, Lightning
} from '@phosphor-icons/react';
import CargaRapidaMetas from '@/components/CargaRapidaMetas';

const formatMoney = (n) => `Q${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const PlanTrabajo = () => {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [branches, setBranches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [triggeringCheck, setTriggeringCheck] = useState(false);
  const [filters, setFilters] = useState({
    branch: '',
    mode: 'days',  // 'days' | 'range'
    days: 30,
    from: daysAgoIso(30),
    to: todayIso(),
  });
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [goalModalUser, setGoalModalUser] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingMetas, setExportingMetas] = useState({ pdf: false, xls: false });

  const buildParams = () => {
    const p = new URLSearchParams();
    if (filters.branch) p.set('branch', filters.branch);
    if (filters.mode === 'range') {
      p.set('from_date', filters.from);
      p.set('to_date', filters.to);
    } else {
      p.set('days', String(filters.days));
    }
    return p;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const tParams = new URLSearchParams(params);
      const [d, b, a, t] = await Promise.all([
        api.get(`/work-plan/dashboard/team?${params.toString()}`),
        api.get('/work-plan/branches'),
        api.get(`/work-plan/activities?${params.toString()}`),
        api.get(`/work-plan/activities/timeline?${tParams.toString()}`),
      ]);
      setData(d.data);
      setBranches(b.data.branches || []);
      setActivities(a.data || []);
      setTimeline(t.data);
    } catch (e) {
      toast.error('Error cargando plan de trabajo del equipo');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const res = await api.get('/work-plan/alerts/inactive-vendors');
      setAlerts(res.data.alerts || []);
    } catch (e) {
      // 403 si no es supervisor/admin: ignorar
    } finally {
      setAlertsLoading(false);
    }
  };

  const triggerCheckNow = async () => {
    setTriggeringCheck(true);
    try {
      const res = await api.post('/work-plan/alerts/check-now?send_notifications=true');
      const r = res.data;
      toast.success(`Chequeo completo: ${r.checked} inactivos, ${r.alerts} alertas nuevas, ${r.notified_whatsapp} WhatsApp enviados`);
      fetchAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error ejecutando chequeo');
    } finally {
      setTriggeringCheck(false);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await api.post(`/work-plan/alerts/${alertId}/resolve`);
      toast.success('Alerta marcada como resuelta');
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch (e) {
      toast.error('Error resolviendo alerta');
    }
  };

  useEffect(() => {
    fetchAll();
    fetchAlerts();
  }, [filters.branch, filters.days, filters.mode, filters.from, filters.to]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      const res = await api.get(`/work-plan/dashboard/team/export-pdf?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `plan-trabajo-${filters.mode === 'range' ? `${filters.from}_a_${filters.to}` : `${filters.days}d`}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (e) {
      toast.error('Error generando PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportPlanMetas = async (kind, simple = false) => {
    setExportingMetas(prev => ({ ...prev, [kind]: true }));
    try {
      const now = new Date();
      const qs = `year=${now.getFullYear()}&month=${now.getMonth() + 1}${simple ? '&simple=true' : ''}`;
      const url = kind === 'pdf'
        ? `/work-plan/reports/plan-metas/export/pdf?${qs}`
        : `/work-plan/reports/plan-metas/export/excel?${qs}`;
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = blobUrl;
      const tag = simple ? 'metas-plan' : 'plan-metas';
      a.download = `${tag}-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch {
      toast.error(`Error generando ${kind.toUpperCase()}`);
    } finally {
      setExportingMetas(prev => ({ ...prev, [kind]: false }));
    }
  };

  if (loading && !data) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--bg)]">
        <CircleNotch size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  const totals = data?.totals || {};

  return (
    <div className="p-6 md:p-8 min-h-full" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="plan-trabajo-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-faint)] font-bold">Plan de Trabajo</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight heading-font">Equipo de Ventas</h1>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            {filters.mode === 'range'
              ? `Del ${filters.from} al ${filters.to}`
              : 'Mes actual'} · Comparativa de actividad y resultados
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={filters.branch}
            onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
            className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm"
            data-testid="filter-branch"
          >
            <option value="">Todas las sucursales</option>
            {branches.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>

          {/* Mode toggle */}
          <div className="flex gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-md p-0.5" data-testid="filter-mode">
            <button
              onClick={() => setFilters({ ...filters, mode: 'days' })}
              className={`px-2.5 py-1.5 text-[11px] font-bold rounded ${filters.mode === 'days' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
              data-testid="filter-mode-days"
            >
              Últimos
            </button>
            <button
              onClick={() => setFilters({ ...filters, mode: 'range' })}
              className={`px-2.5 py-1.5 text-[11px] font-bold rounded ${filters.mode === 'range' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
              data-testid="filter-mode-range"
            >
              Rango
            </button>
          </div>

          {filters.mode === 'days' ? (
            <select
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}
              className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm"
              data-testid="filter-days"
            >
              <option value={7}>7 días</option>
              <option value={15}>15 días</option>
              <option value={30}>30 días</option>
              <option value={60}>60 días</option>
              <option value={90}>90 días</option>
            </select>
          ) : (
            <div className="flex gap-1 items-center">
              <input
                type="date"
                value={filters.from}
                max={filters.to}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-2 py-1.5 text-xs"
                data-testid="filter-from"
              />
              <span className="text-[var(--text-faint)] text-xs">→</span>
              <input
                type="date"
                value={filters.to}
                min={filters.from}
                max={todayIso()}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-2 py-1.5 text-xs"
                data-testid="filter-to"
              />
            </div>
          )}

          <CargaRapidaMetas />
          <button
            onClick={exportPdf}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50"
            data-testid="btn-export-pdf"
          >
            {exporting ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />}
            Exportar PDF
          </button>

          {/* Plan + Metas report (dropdown selector) */}
          <div className="relative" data-testid="export-metas-wrapper">
            <select
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const [fmt, mode] = v.split('-');
                exportPlanMetas(fmt, mode === 'simple');
                e.target.value = '';
              }}
              disabled={exportingMetas.pdf || exportingMetas.xls}
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50 cursor-pointer"
              data-testid="export-metas-selector"
            >
              <option value="">📊 Reporte Metas/Plan ▾</option>
              <optgroup label="Solo Metas + Plan (sin cumplimiento)">
                <option value="pdf-simple">  ↳ PDF — Metas CYH/SA/Total + Plan diario</option>
                <option value="xls-simple">  ↳ Excel — Metas CYH/SA/Total + Plan diario</option>
              </optgroup>
              <optgroup label="Detallado (con cumplimiento %)">
                <option value="pdf-detail">  ↳ PDF — Metas + Cumplimiento</option>
                <option value="xls-detail">  ↳ Excel — Metas + Cumplimiento</option>
              </optgroup>
            </select>
            {(exportingMetas.pdf || exportingMetas.xls) && (
              <CircleNotch size={14} className="animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-white pointer-events-none" />
            )}
          </div>
        </div>
      </div>

      {/* Timeline chart */}
      {timeline && timeline.dates && timeline.dates.length > 0 && (
        <TimelineChart timeline={timeline} />
      )}

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <TotalCard label="Llamadas" value={totals.calls || 0} icon={Phone} color="blue" />
        <TotalCard label="Visitas" value={totals.visits || 0} icon={MapPin} color="amber" />
        <TotalCard label="Cotizaciones" value={totals.quotes || 0} icon={FileText} color="purple" sub={formatMoney(totals.quotes_amount)} />
        <TotalCard label="Ventas" value={totals.sales || 0} icon={CurrencyDollar} color="emerald" sub={formatMoney(totals.sales_amount)} highlight />
      </div>

      {/* Inactive vendor alerts (rich card with resolve) */}
      <InactiveAlertsCard
        alerts={alerts}
        loading={alertsLoading}
        triggering={triggeringCheck}
        onTriggerCheck={triggerCheckNow}
        onResolve={resolveAlert}
      />

      {/* By branch */}
      {data?.by_branch?.length > 0 && (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Buildings size={20} weight="bold" className="text-blue-400" />
            <h3 className="text-sm font-black heading-font uppercase tracking-wide">Por Sucursal</h3>
          </div>
          <div className="space-y-2" data-testid="branch-rows">
            {data.by_branch.map((b) => (
              <div key={b.branch} className="border border-[var(--border)] rounded-[10px] p-3 grid grid-cols-2 md:grid-cols-7 gap-2 items-center hover:bg-white/[0.02]" data-testid={`branch-row-${b.branch}`}>
                <div className="md:col-span-2">
                  <p className="text-sm font-bold text-[var(--text)]">{b.branch}</p>
                  <p className="text-[10px] text-[var(--text-faint)]">{b.vendors} vendedor{b.vendors === 1 ? '' : 'es'}</p>
                </div>
                <BranchStat label="Llamadas" value={b.calls} />
                <BranchStat label="Visitas" value={b.visits} />
                <BranchStat label="Cotizac." value={b.quotes} />
                <BranchStat label="Ventas" value={b.sales} highlight />
                <div className="text-right">
                  <p className="text-[9px] text-[var(--text-faint)] uppercase font-bold">Total ventas</p>
                  <p className="text-sm font-black text-emerald-400">{formatMoney(b.sales_amount)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per vendor */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Users size={20} weight="bold" className="text-blue-400" />
          <h3 className="text-sm font-black heading-font uppercase tracking-wide">Por Vendedor</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendors-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] border-b border-[var(--border)]">
                <th className="text-left py-2 pr-2">Vendedor</th>
                <th className="text-center py-2">Llamadas</th>
                <th className="text-center py-2">Visitas</th>
                <th className="text-center py-2">Cotizac.</th>
                <th className="text-center py-2">Ventas</th>
                <th className="text-right py-2">$ Ventas</th>
                <th className="text-right py-2">Meta</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.per_vendor || []).map((v) => (
                <tr key={v.user_id} className="border-b border-[var(--border)] hover:bg-white/[0.02]" data-testid={`vendor-row-${v.user_id}`}>
                  <td className="py-2.5 pr-2">
                    <p className="font-bold text-[var(--text)]">{v.user_name}</p>
                    <p className="text-[10px] text-[var(--text-faint)]">{v.branch || 'sin sucursal'} · {v.role}</p>
                  </td>
                  <td className="text-center text-blue-400 font-bold">{v.kpis?.calls || 0}</td>
                  <td className="text-center text-amber-400 font-bold">{v.kpis?.visits || 0}</td>
                  <td className="text-center text-purple-400 font-bold">{v.kpis?.quotes || 0}</td>
                  <td className="text-center text-emerald-400 font-black">{v.kpis?.sales || 0}</td>
                  <td className="text-right text-emerald-400 font-black">{formatMoney(v.kpis?.sales_amount)}</td>
                  <td className="text-right">
                    {v.goal_target ? (
                      <div>
                        <p className="text-[10px] text-[var(--text-muted)]">{formatMoney(v.goal_target)}</p>
                        <p className={`text-xs font-black ${v.goal_percent >= 100 ? 'text-emerald-400' : v.goal_percent >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
                          {v.goal_percent}%
                        </p>
                      </div>
                    ) : (
                      <span className="text-[10px] text-[var(--text-faint)]">Sin meta</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <div className="flex justify-end gap-2">
                      <a
                        href={`/reporte-actividades?user_id=${v.user_id}`}
                        className="text-[10px] font-bold text-purple-400 hover:text-purple-300 underline"
                        data-testid={`view-detail-${v.user_id}`}
                      >
                        <MagnifyingGlass size={12} weight="bold" className="inline mr-1" />
                        Detalle
                      </a>
                      <button
                        onClick={() => setGoalModalUser(v)}
                        className="text-[10px] font-bold text-blue-400 hover:text-blue-300 underline"
                        data-testid={`set-goal-${v.user_id}`}
                      >
                        <Target size={12} weight="bold" className="inline mr-1" />
                        Meta
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(data?.per_vendor || []).length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-[var(--text-faint)] text-xs">Sin vendedores que coincidan con el filtro</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent activity feed */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5">
        <div className="flex items-center gap-2 mb-4">
          <ListChecks size={20} weight="bold" className="text-blue-400" />
          <h3 className="text-sm font-black heading-font uppercase tracking-wide">Actividad reciente</h3>
          <span className="ml-auto text-[10px] text-[var(--text-faint)] font-bold">{activities.length} registros</span>
        </div>
        <div className="space-y-1.5 max-h-[400px] overflow-y-auto" data-testid="team-activity-feed">
          {activities.slice(0, 100).map((a) => (
            <TeamActivityRow key={a.id} activity={a} />
          ))}
          {activities.length === 0 && (
            <p className="text-center py-8 text-xs text-[var(--text-faint)]">Sin actividad registrada en este periodo</p>
          )}
        </div>
      </div>

      <GoalModal
        vendor={goalModalUser}
        onClose={() => setGoalModalUser(null)}
        onSaved={() => { setGoalModalUser(null); fetchAll(); }}
      />
    </div>
  );
};

const InactiveAlertsCard = ({ alerts, loading, triggering, onTriggerCheck, onResolve }) => {
  if (!alerts || alerts.length === 0) {
    return (
      <div
        className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 mb-6 flex items-center justify-between gap-3 shadow-[var(--shadow-sm)]"
        data-testid="inactive-alerts-empty"
      >
        <div className="flex items-center gap-3">
          <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0" />
          <div>
            <p className="text-sm font-bold text-[var(--text)]">Equipo activo</p>
            <p className="text-xs text-[var(--text-muted)]">No hay vendedores marcados como inactivos.</p>
          </div>
        </div>
        <button
          onClick={onTriggerCheck}
          disabled={triggering}
          className="text-[11px] font-bold px-3 py-1.5 rounded-md bg-[var(--surface-muted)] border border-[var(--border)] hover:bg-[var(--bg)] text-[var(--text)] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="btn-trigger-check"
        >
          {triggering ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} weight="bold" />}
          Chequear ahora
        </button>
      </div>
    );
  }

  return (
    <div className="bg-red-500/5 dark:bg-red-500/10 border border-red-500/30 rounded-[14px] p-5 mb-6 shadow-[var(--shadow-sm)]" data-testid="inactive-alerts-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <BellRinging size={20} weight="fill" className="text-red-500 animate-pulse" />
        <h3 className="text-sm font-black heading-font uppercase tracking-wide text-red-600 dark:text-red-400">
          {alerts.length} vendedor{alerts.length === 1 ? '' : 'es'} sin actividad
        </h3>
        <span className="text-[10px] text-[var(--text-faint)] font-bold ml-1">
          (≥3 días sin llamadas/visitas/cotizaciones)
        </span>
        <button
          onClick={onTriggerCheck}
          disabled={triggering || loading}
          className="ml-auto text-[11px] font-bold px-3 py-1.5 rounded-md bg-red-500/15 border border-red-500/30 hover:bg-red-500/25 text-red-600 dark:text-red-400 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="btn-trigger-check"
        >
          {triggering ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} weight="bold" />}
          Notificar ahora
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {alerts.map((a) => (
          <div
            key={a.id}
            className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-3 flex items-center justify-between gap-2"
            data-testid={`alert-row-${a.user_id}`}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-black text-[var(--text)] truncate">{a.user_name}</p>
              <p className="text-[10px] text-[var(--text-muted)] truncate">
                {a.branch || 'sin sucursal'} · {a.days_inactive}d sin actividad
              </p>
              {a.last_activity && (
                <p className="text-[10px] text-[var(--text-faint)]">
                  Última: {new Date(a.last_activity).toLocaleDateString('es-GT')}
                </p>
              )}
            </div>
            <button
              onClick={() => onResolve(a.id)}
              className="text-[10px] font-bold px-2 py-1.5 rounded bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 text-emerald-600 dark:text-emerald-400 shrink-0 flex items-center gap-1"
              data-testid={`btn-resolve-${a.user_id}`}
              title="Marcar como atendida"
            >
              <CheckCircle size={11} weight="bold" />
              Resolver
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const TimelineChart = ({ timeline }) => {
  const series = timeline.series || {};
  const dates = timeline.dates || [];
  const maxCount = Math.max(
    1,
    ...dates.map((_, i) =>
      (series.calls?.[i] || 0) +
      (series.visits?.[i] || 0) +
      (series.quotes?.[i] || 0) +
      (series.sales?.[i] || 0)
    )
  );
  const width = 100;
  const barW = width / Math.max(dates.length, 1);

  // Stack heights as percentages of maxCount
  const stacks = dates.map((d, i) => {
    const c = series.calls?.[i] || 0;
    const v = series.visits?.[i] || 0;
    const q = series.quotes?.[i] || 0;
    const s = series.sales?.[i] || 0;
    const total = c + v + q + s;
    return { date: d, c, v, q, s, total };
  });

  const totalActs = stacks.reduce((acc, x) => acc + x.total, 0);
  const totalSales = (series.sales_amount || []).reduce((a, b) => a + (b || 0), 0);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6 shadow-[var(--shadow-sm)]" data-testid="timeline-chart">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <ChartLineUp size={20} weight="bold" className="text-[var(--primary)]" />
        <h3 className="text-sm font-black heading-font uppercase tracking-wide">Tendencia de actividad</h3>
        <span className="ml-auto text-[10px] text-[var(--text-faint)] font-bold">
          {totalActs} actividades · {formatMoney(totalSales)} en ventas
        </span>
      </div>

      <div className="relative w-full h-[140px] overflow-hidden">
        <svg viewBox={`0 0 ${width} 100`} preserveAspectRatio="none" className="w-full h-full" aria-label="Tendencia diaria">
          {stacks.map((b, i) => {
            const x = i * barW;
            const h = (b.total / maxCount) * 92;
            let y = 96 - h;
            const cH = (b.c / maxCount) * 92;
            const vH = (b.v / maxCount) * 92;
            const qH = (b.q / maxCount) * 92;
            const sH = (b.s / maxCount) * 92;
            const pad = barW * 0.18;
            const w = Math.max(0.4, barW - pad * 2);
            return (
              <g key={b.date}>
                <title>{`${b.date}\nLlamadas: ${b.c} · Visitas: ${b.v} · Cotiz.: ${b.q} · Ventas: ${b.s}`}</title>
                {sH > 0 && <rect x={x + pad} y={y} width={w} height={sH} fill="#10B981" />}
                {qH > 0 && <rect x={x + pad} y={y + sH} width={w} height={qH} fill="#A855F7" />}
                {vH > 0 && <rect x={x + pad} y={y + sH + qH} width={w} height={vH} fill="#F59E0B" />}
                {cH > 0 && <rect x={x + pad} y={y + sH + qH + vH} width={w} height={cH} fill="#3B82F6" />}
                {b.total === 0 && <rect x={x + pad} y={95.4} width={w} height={0.6} fill="var(--border)" />}
              </g>
            );
          })}
          <line x1="0" y1="96" x2={width} y2="96" stroke="var(--border)" strokeWidth="0.4" />
        </svg>
      </div>

      <div className="flex items-center justify-between mt-2">
        <p className="text-[9px] text-[var(--text-faint)] font-bold">{dates[0]}</p>
        <div className="flex gap-3 text-[9px] font-bold uppercase tracking-wide flex-wrap justify-center">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#3B82F6' }} />Llamadas</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#F59E0B' }} />Visitas</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#A855F7' }} />Cotiz.</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#10B981' }} />Ventas</span>
        </div>
        <p className="text-[9px] text-[var(--text-faint)] font-bold">{dates[dates.length - 1]}</p>
      </div>
    </div>
  );
};

const TotalCard = ({ label, value, icon: Icon, color, sub, highlight }) => {
  const colors = {
    blue: 'text-blue-400 border-blue-500/20 bg-blue-500/5',
    amber: 'text-amber-400 border-amber-500/20 bg-amber-500/5',
    purple: 'text-purple-400 border-purple-500/20 bg-purple-500/5',
    emerald: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5',
  };
  return (
    <div className={`border rounded-[12px] p-4 ${colors[color]} ${highlight ? 'ring-2 ring-emerald-500/30' : ''}`} data-testid={`total-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={16} weight="bold" />
        <span className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</span>
      </div>
      <p className="text-3xl font-black">{value}</p>
      {sub && <p className="text-xs font-bold opacity-80 mt-0.5">{sub}</p>}
    </div>
  );
};

const BranchStat = ({ label, value, highlight }) => (
  <div className="text-center">
    <p className="text-[9px] text-[var(--text-faint)] uppercase font-bold">{label}</p>
    <p className={`text-lg font-black ${highlight ? 'text-emerald-400' : 'text-[var(--text)]'}`}>{value}</p>
  </div>
);

const ICONS = { call: Phone, visit: MapPin, quote: FileText, sale: CurrencyDollar };
const COLORS = { call: 'text-blue-400', visit: 'text-amber-400', quote: 'text-purple-400', sale: 'text-emerald-400' };
const LABELS = { call: 'Llamada', visit: 'Visita', quote: 'Cotización', sale: 'Venta' };

const TeamActivityRow = ({ activity: a }) => {
  const Icon = ICONS[a.type] || Phone;
  const color = COLORS[a.type] || 'text-[var(--text-muted)]';
  return (
    <div className="flex items-center gap-2 py-1.5 px-2 hover:bg-white/[0.02] rounded text-xs" data-testid={`team-activity-${a.id}`}>
      <Icon size={14} weight="fill" className={color} />
      <span className="text-[var(--text-muted)] w-24 truncate font-bold">{a.user_name}</span>
      <span className={`${color} font-bold w-20 shrink-0`}>{LABELS[a.type]}</span>
      <span className="truncate flex-1">{a.customer_name}</span>
      {a.amount != null && <span className="text-emerald-400 font-bold shrink-0">{formatMoney(a.amount)}</span>}
      <span className="text-[var(--text-faint)] text-[10px] shrink-0">{new Date(a.happened_at).toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit' })}</span>
    </div>
  );
};

const GoalModal = ({ vendor, onClose, onSaved }) => {
  const [form, setForm] = useState({
    target_amount: '', commission_rate_at_target: '', commission_rate_below: '',
    daily_calls: '', daily_visits: '', daily_quotes: '',
    monthly_calls: '', monthly_visits: '', monthly_quotes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vendor) return;
    const now = new Date();
    api.get(`/work-plan/goals?user_id=${vendor.user_id}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`)
      .then((res) => {
        const g = (res.data || [])[0] || {};
        setForm({
          target_amount: g.target_amount ?? vendor.goal_target ?? '',
          commission_rate_at_target: g.commission_rate_at_target != null ? (g.commission_rate_at_target * 100).toFixed(1) : '',
          commission_rate_below: g.commission_rate_below != null ? (g.commission_rate_below * 100).toFixed(1) : '',
          daily_calls: g.daily_calls ?? '',
          daily_visits: g.daily_visits ?? '',
          daily_quotes: g.daily_quotes ?? '',
          monthly_calls: g.monthly_calls ?? '',
          monthly_visits: g.monthly_visits ?? '',
          monthly_quotes: g.monthly_quotes ?? '',
        });
      })
      .catch(() => {
        setForm((f) => ({ ...f, target_amount: vendor.goal_target || '' }));
      });
  }, [vendor]);

  if (!vendor) return null;
  const now = new Date();
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const pct = (v) => (v === '' || v == null ? null : Number(v) / 100);

  const save = async () => {
    if (!form.target_amount || isNaN(parseFloat(form.target_amount))) {
      toast.error('Meta de ventas requerida (Q)');
      return;
    }
    setSaving(true);
    try {
      await api.put('/work-plan/goals', {
        user_id: vendor.user_id,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        target_amount: parseFloat(form.target_amount),
        commission_rate_at_target: pct(form.commission_rate_at_target),
        commission_rate_below: pct(form.commission_rate_below),
        daily_calls: num(form.daily_calls),
        daily_visits: num(form.daily_visits),
        daily_quotes: num(form.daily_quotes),
        monthly_calls: num(form.monthly_calls),
        monthly_visits: num(form.monthly_visits),
        monthly_quotes: num(form.monthly_quotes),
      });
      toast.success('Metas guardadas ✅');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando metas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!vendor} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="goal-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target size={20} weight="fill" className="text-emerald-500" />
            Metas del mes — {vendor.user_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-[var(--text-muted)]">
            {new Date(now.getFullYear(), now.getMonth()).toLocaleDateString('es-GT', { month: 'long', year: 'numeric' })}
          </p>

          {/* Ventas */}
          <div className="space-y-2">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-muted)]">💰 Ventas</p>
            <div>
              <Label>Meta de ventas (Q) *</Label>
              <Input type="number" step="100" value={form.target_amount}
                onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                placeholder="Ej: 50000" className="bg-[var(--bg)] border-[var(--border)]"
                data-testid="goal-target" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">% si cumple</Label>
                <Input type="number" step="0.1" value={form.commission_rate_at_target}
                  onChange={(e) => setForm({ ...form, commission_rate_at_target: e.target.value })}
                  placeholder="5" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-rate-at" />
              </div>
              <div>
                <Label className="text-[11px]">% si no cumple</Label>
                <Input type="number" step="0.1" value={form.commission_rate_below}
                  onChange={(e) => setForm({ ...form, commission_rate_below: e.target.value })}
                  placeholder="2" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-rate-below" />
              </div>
            </div>
          </div>

          {/* Diarias */}
          <div className="space-y-2 pt-3 border-t border-[var(--border)]">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-muted)]">📅 Metas Diarias <span className="opacity-60 lowercase tracking-normal font-normal">(opcional)</span></p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px]">📞 Llamadas</Label>
                <Input type="number" value={form.daily_calls}
                  onChange={(e) => setForm({ ...form, daily_calls: e.target.value })}
                  placeholder="5" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-daily-calls" />
              </div>
              <div>
                <Label className="text-[11px]">📍 Visitas</Label>
                <Input type="number" value={form.daily_visits}
                  onChange={(e) => setForm({ ...form, daily_visits: e.target.value })}
                  placeholder="2" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-daily-visits" />
              </div>
              <div>
                <Label className="text-[11px]">📄 Cotiz.</Label>
                <Input type="number" value={form.daily_quotes}
                  onChange={(e) => setForm({ ...form, daily_quotes: e.target.value })}
                  placeholder="3" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-daily-quotes" />
              </div>
            </div>
          </div>

          {/* Mensuales */}
          <div className="space-y-2 pt-3 border-t border-[var(--border)]">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-muted)]">📆 Metas Mensuales <span className="opacity-60 lowercase tracking-normal font-normal">(opcional)</span></p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px]">📞 Llamadas</Label>
                <Input type="number" value={form.monthly_calls}
                  onChange={(e) => setForm({ ...form, monthly_calls: e.target.value })}
                  placeholder="100" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-monthly-calls" />
              </div>
              <div>
                <Label className="text-[11px]">📍 Visitas</Label>
                <Input type="number" value={form.monthly_visits}
                  onChange={(e) => setForm({ ...form, monthly_visits: e.target.value })}
                  placeholder="40" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-monthly-visits" />
              </div>
              <div>
                <Label className="text-[11px]">📄 Cotiz.</Label>
                <Input type="number" value={form.monthly_quotes}
                  onChange={(e) => setForm({ ...form, monthly_quotes: e.target.value })}
                  placeholder="60" className="bg-[var(--bg)] border-[var(--border)]" data-testid="goal-monthly-quotes" />
              </div>
            </div>
          </div>

          <p className="text-[10px] text-[var(--text-faint)]">Solo la meta de ventas es obligatoria. Las metas de actividad son opcionales.</p>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 border-[var(--border)]" data-testid="goal-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="goal-save">
              {saving ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Target size={16} weight="bold" className="mr-2" />}
              Guardar metas
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanTrabajo;
